import { type FormEvent, type ReactNode, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { ActionIconButton, DataViewActionCell } from '../components/DataViewActionCell';
import { DataTable, type TableColumn } from '../components/DataTable';
import { EntityCreateDialog, EntityManageDialog } from '../components/EntityDialogs';
import { PageHeader } from '../components/PageHeader';
import { useToast } from '../components/Toast';
import { Button, Card, CheckboxField, Field, Select, TextArea, TextInput } from '../components/ui';
import { useAuth } from '../contexts/AuthContext';
import { usePaginatedResource } from '../hooks/usePaginatedResource';
import { useT, userError } from '../i18n/messages';
import {
  describeError,
  emptyPriceForm,
  emptyProductForm,
  formatPrice,
  formatMetadata,
  formatProviderMappings,
  toPriceInput,
  toProductInput,
  type PriceFormState,
  type ProductFormState,
} from '../lib/adminForms';
import {
  type ApplicationRecord,
  type PriceRecord,
  type ProductRecord,
  billingApi,
} from '../lib/api';
import {
  optionLabel,
  billingIntervalOptions,
  currencyOptions,
  priceStatusOptions,
  productStatusOptions,
  productTypeOptions,
  productVisibilityOptions,
} from '../lib/options';

function Section({
  actions,
  children,
  title,
}: {
  actions?: ReactNode;
  children: ReactNode;
  title: string;
}) {
  return (
    <section className="panel page-panel">
      <div className="panel-heading">
        <h2>{title}</h2>
        {actions}
      </div>
      {children}
    </section>
  );
}

function ConfirmDialog({
  busy = false,
  description,
  onClose,
  onConfirm,
  open,
  title,
}: {
  busy?: boolean;
  description: string;
  onClose: () => void;
  onConfirm: () => void;
  open: boolean;
  title: string;
}) {
  const t = useT();
  return (
    <EntityManageDialog onClose={onClose} open={open} size="small" title={title}>
      <div className="entity-stack">
        <p>{description}</p>
        <div className="dialog-actions">
          <Button disabled={busy} onClick={onClose} type="button" variant="secondary">
            {t('billing.ui.cancel')}
          </Button>
          <Button disabled={busy} onClick={onConfirm} type="button" variant="danger">
            {t('billing.ui.confirm')}
          </Button>
        </div>
      </div>
    </EntityManageDialog>
  );
}

function ProductFormFields({
  applications,
  form,
  onChange,
}: {
  applications: ApplicationRecord[];
  form: ProductFormState;
  onChange: (next: ProductFormState) => void;
}) {
  const t = useT();
  return (
    <div className="form-grid">
      <Field label={t('billing.column.application')}>
        <Select
          onChange={(value) => onChange({ ...form, application_id: value ?? '' })}
          options={applications.map((application) => ({
            label: `${application.display_name} (${application.key})`,
            value: application.id,
          }))}
          placeholder={t('billing.ui.select.an.application')}
          value={form.application_id}
        />
      </Field>
      <div className="form-row form-row--split">
        <Field label={t('billing.ui.product.key')}>
          <TextInput
            onChange={(event) => onChange({ ...form, key: event.target.value })}
            placeholder="pro_monthly"
            value={form.key}
          />
        </Field>
        <Field label={t('billing.column.displayName')}>
          <TextInput
            onChange={(event) => onChange({ ...form, display_name: event.target.value })}
            placeholder={t('billing.ui.pro.monthly')}
            value={form.display_name}
          />
        </Field>
      </div>
      <div className="form-row form-row--split">
        <Field label={t('billing.column.status')}>
          <Select
            onChange={(value) => onChange({ ...form, status: value ?? 'draft' })}
            options={productStatusOptions}
            value={form.status}
          />
        </Field>
        <Field label={t('billing.ui.visibility')}>
          <Select
            onChange={(value) => onChange({ ...form, visibility: value ?? 'public' })}
            options={productVisibilityOptions}
            value={form.visibility}
          />
        </Field>
      </div>
      <div className="form-row form-row--split">
        <Field label={t('billing.column.productType')}>
          <Select
            onChange={(value) => onChange({ ...form, product_type: value ?? 'subscription' })}
            options={productTypeOptions}
            value={form.product_type}
          />
        </Field>
        <CheckboxField
          checked={form.is_featured}
          description={t('billing.ui.featured.products.can.be.highlighted.in.public.catalog.views')}
          label={t('billing.ui.featured')}
          onChange={(checked) => onChange({ ...form, is_featured: checked })}
        />
      </div>
      <Field label={t('billing.column.description')}>
        <TextArea
          onChange={(event) => onChange({ ...form, description: event.target.value })}
          placeholder={t('billing.ui.short.description.for.administrators')}
          rows={3}
          value={form.description}
        />
      </Field>
      <Field
        description={t('billing.ui.json.object.persisted.with.the.product')}
        label={t('billing.ui.metadata')}
      >
        <TextArea
          onChange={(event) => onChange({ ...form, metadataText: event.target.value })}
          placeholder={'{"tier":"pro"}'}
          rows={5}
          value={form.metadataText}
        />
      </Field>
      <Field
        description={t(
          'billing.ui.one.mapping.per.line.using.provider.key.environment.status.provider.product.id.provider.price.id.metadatajson',
        )}
        label={t('billing.column.providerMappings')}
      >
        <TextArea
          onChange={(event) => onChange({ ...form, providerMappingsText: event.target.value })}
          placeholder={'polar|prod|active|prod_123|price_123|{"channel":"default"}'}
          rows={6}
          value={form.providerMappingsText}
        />
      </Field>
    </div>
  );
}

function PriceFormFields({
  form,
  onChange,
}: {
  form: PriceFormState;
  onChange: (next: PriceFormState) => void;
}) {
  const t = useT();
  return (
    <div className="form-grid">
      <div className="form-row form-row--split">
        <Field label={t('billing.ui.price.key')}>
          <TextInput
            onChange={(event) => onChange({ ...form, key: event.target.value })}
            placeholder="monthly"
            value={form.key}
          />
        </Field>
        <Field label={t('billing.column.displayName')}>
          <TextInput
            onChange={(event) => onChange({ ...form, display_name: event.target.value })}
            placeholder={t('billing.ui.monthly')}
            value={form.display_name}
          />
        </Field>
      </div>
      <Field label={t('billing.column.description')}>
        <TextArea
          onChange={(event) => onChange({ ...form, description: event.target.value })}
          placeholder={t('billing.ui.short.description.for.administrators')}
          rows={3}
          value={form.description}
        />
      </Field>
      <div className="form-row form-row--split">
        <Field label={t('billing.column.currency')}>
          <Select
            onChange={(value) => onChange({ ...form, currency: value ?? 'USD' })}
            options={currencyOptions}
            value={form.currency}
          />
        </Field>
        <Field label={t('billing.column.amount')}>
          <TextInput
            inputMode="numeric"
            onChange={(event) => onChange({ ...form, amount: event.target.value })}
            placeholder="1000"
            value={form.amount}
          />
        </Field>
      </div>
      <div className="form-row form-row--split">
        <Field label={t('billing.ui.billing.interval')}>
          <Select
            onChange={(value) => onChange({ ...form, billing_interval: value ?? 'month' })}
            options={billingIntervalOptions}
            value={form.billing_interval}
          />
        </Field>
        <Field label={t('billing.column.status')}>
          <Select
            onChange={(value) => onChange({ ...form, status: value ?? 'active' })}
            options={priceStatusOptions}
            value={form.status}
          />
        </Field>
      </div>
      <div className="form-row form-row--split">
        <Field label={t('billing.ui.trial.days')}>
          <TextInput
            inputMode="numeric"
            onChange={(event) => onChange({ ...form, trial_days: event.target.value })}
            placeholder="14"
            value={form.trial_days}
          />
        </Field>
        <Field label={t('billing.ui.compare.at.amount')}>
          <TextInput
            inputMode="numeric"
            onChange={(event) => onChange({ ...form, compare_at_amount: event.target.value })}
            placeholder="1200"
            value={form.compare_at_amount}
          />
        </Field>
      </div>
      <div className="form-row form-row--split">
        <Field label={t('billing.ui.display.badge')}>
          <TextInput
            onChange={(event) => onChange({ ...form, display_badge: event.target.value })}
            placeholder={t('billing.ui.best.value')}
            value={form.display_badge}
          />
        </Field>
        <Field label={t('billing.ui.sort.order')}>
          <TextInput
            inputMode="numeric"
            onChange={(event) => onChange({ ...form, sort_order: event.target.value })}
            placeholder="0"
            value={form.sort_order}
          />
        </Field>
      </div>
      <div className="form-row form-row--split">
        <CheckboxField
          checked={form.is_default}
          label={t('billing.ui.default.price')}
          onChange={(checked) => onChange({ ...form, is_default: checked })}
        />
        <CheckboxField
          checked={form.is_recommended}
          label={t('billing.ui.recommended')}
          onChange={(checked) => onChange({ ...form, is_recommended: checked })}
        />
      </div>
      <Field
        description={t('billing.ui.json.object.persisted.with.the.price')}
        label={t('billing.ui.metadata')}
      >
        <TextArea
          onChange={(event) => onChange({ ...form, metadataText: event.target.value })}
          placeholder={'{"tier":"pro"}'}
          rows={5}
          value={form.metadataText}
        />
      </Field>
      <Field
        description={t(
          'billing.ui.one.mapping.per.line.using.provider.key.environment.status.provider.product.id.provider.price.id.metadatajson',
        )}
        label={t('billing.column.providerMappings')}
      >
        <TextArea
          onChange={(event) => onChange({ ...form, providerMappingsText: event.target.value })}
          placeholder={'polar|prod|active|prod_123|price_123|{"channel":"default"}'}
          rows={6}
          value={form.providerMappingsText}
        />
      </Field>
    </div>
  );
}

export function ProductsPage() {
  const { tokens } = useAuth();
  const t = useT();
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedApplicationId = searchParams.get('application_id') ?? '';
  const [page, setPage] = useState(1);
  const [applications, setApplications] = useState<ApplicationRecord[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [productForm, setProductForm] = useState<ProductFormState>(emptyProductForm);
  const resource = usePaginatedResource<ProductRecord>(
    tokens?.access_token ?? '',
    page,
    billingApi.getProducts,
  );
  const filteredProducts = useMemo(() => {
    const products = resource.status === 'success' && resource.data ? resource.data.items : [];
    return selectedApplicationId
      ? products.filter((product) => product.application_id === selectedApplicationId)
      : products;
  }, [resource.data, resource.status, selectedApplicationId]);

  function updateApplicationFilter(applicationId: string | null) {
    setPage(1);
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (applicationId) {
        next.set('application_id', applicationId);
      } else {
        next.delete('application_id');
      }
      return next;
    });
  }

  useEffect(() => {
    if (!tokens?.access_token) {
      return;
    }

    const controller = new AbortController();
    billingApi
      .listApplications(tokens.access_token, controller.signal)
      .then((response) => {
        setApplications(response.items);
        setProductForm((current) =>
          current.application_id
            ? current
            : { ...current, application_id: response.items[0]?.id ?? '' },
        );
      })
      .catch((error) => {
        if (controller.signal.aborted) {
          return;
        }
        toast.error(describeError(error));
      });

    return () => {
      controller.abort();
    };
  }, [tokens?.access_token]);

  const columns: TableColumn<ProductRecord>[] = [
    {
      key: 'application_display_name',
      label: 'billing.column.application',
      render: (product) => product.application_display_name,
      searchValue: (product) => `${product.application_display_name} ${product.application_key}`,
    },
    { key: 'key', label: 'billing.column.key' },
    { key: 'display_name', label: 'billing.column.displayName' },
    {
      key: 'visibility',
      label: 'billing.column.details',
      render: (product) => optionLabel(productVisibilityOptions, product.visibility),
      maxWidth: 120,
    },
    {
      key: 'status',
      label: 'billing.column.status',
      render: (product) => optionLabel(productStatusOptions, product.status),
      maxWidth: 120,
    },
    {
      key: 'prices',
      label: 'billing.column.prices',
      render: (product) => String(product.prices.length),
      sortValue: (product) => product.prices.length,
      maxWidth: 100,
    },
    {
      key: 'actions',
      label: 'billing.column.actions',
      className: 'table-actions-cell',
      truncate: false,
      render: (product) => (
        <DataViewActionCell>
          <ActionIconButton
            icon="manage"
            label={t('billing.ui.product.settings')}
            onClick={() => navigate(`/products/${product.id}`)}
          />
        </DataViewActionCell>
      ),
    },
  ];

  async function submitCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!tokens?.access_token) {
      return;
    }

    try {
      const created = await billingApi.createProduct(
        tokens.access_token,
        toProductInput(productForm),
      );
      toast.success(t('billing.ui.product.created'));
      setCreateOpen(false);
      setProductForm((current) => ({
        ...emptyProductForm,
        application_id: current.application_id,
      }));
      resource.reload();
      navigate(`/products/${created.id}`);
    } catch (error) {
      toast.error(describeError(error));
    }
  }

  if (!tokens) {
    return <section className="panel page-panel">{t('billing.error.unauthorized')}</section>;
  }

  return (
    <section className="page" data-testid="billing-products-page">
      <PageHeader
        description={t('billing.ui.products.are.edited.from.their.dedicated.manage.pages')}
        title={t('billing.page.products')}
        actions={
          <Button onClick={() => setCreateOpen(true)} type="button">
            {t('billing.ui.create.product')}
          </Button>
        }
      />

      {resource.status === 'loading' ? (
        <section className="panel page-panel">{t('billing.resource.loading')}</section>
      ) : null}

      {resource.status === 'error' ? (
        <Card className="panel panel-error page-panel">
          <p>{userError(resource.error, t)}</p>
          <Button onClick={resource.reload} type="button">
            {t('billing.resource.retry')}
          </Button>
        </Card>
      ) : null}

      {resource.status === 'success' && resource.data ? (
        <>
          <Card className="panel page-panel">
            <div className="panel-heading">
              <h3>{t('billing.ui.filters')}</h3>
            </div>
            <div className="filter-grid">
              <Field label={t('billing.column.application')}>
                <Select
                  onChange={updateApplicationFilter}
                  options={applications.map((application) => ({
                    label: application.display_name,
                    value: application.id,
                  }))}
                  placeholder={t('billing.ui.any.application')}
                  value={selectedApplicationId}
                />
              </Field>
            </div>
          </Card>
          <DataTable
            columns={columns}
            emptyMessage={t('billing.ui.no.products.have.been.created.yet')}
            rows={filteredProducts}
            searchable
            searchPlaceholder={t('billing.ui.search.products')}
          />
          <footer className="pagination">
            <Button
              disabled={page <= 1}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              type="button"
              variant="secondary"
            >
              {t('billing.resource.previous')}
            </Button>
            <span className="pagination-summary">
              {t('billing.pagination.summary', {
                page: resource.data.pagination.page,
                total: Math.max(resource.data.pagination.total_pages, 1),
              })}
            </span>
            <Button
              disabled={
                resource.data.pagination.total_pages === 0 ||
                page >= resource.data.pagination.total_pages
              }
              onClick={() => setPage((current) => current + 1)}
              type="button"
              variant="secondary"
            >
              {t('billing.resource.next')}
            </Button>
          </footer>
        </>
      ) : null}

      <EntityCreateDialog
        onClose={() => setCreateOpen(false)}
        open={createOpen}
        size="large"
        title={t('billing.ui.create.product')}
      >
        <form onSubmit={(event) => void submitCreate(event)}>
          <ProductFormFields
            applications={applications}
            form={productForm}
            onChange={setProductForm}
          />
          <div className="dialog-actions">
            <Button type="submit">{t('billing.ui.create.product')}</Button>
            <Button onClick={() => setCreateOpen(false)} type="button" variant="secondary">
              {t('billing.ui.cancel')}
            </Button>
          </div>
        </form>
      </EntityCreateDialog>
    </section>
  );
}

export function ProductManagePage() {
  const t = useT();
  const { productId, priceId } = useParams();
  const { tokens } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [applications, setApplications] = useState<ApplicationRecord[]>([]);
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [banner, setBanner] = useState<{
    error: boolean;
    message: string;
  } | null>(null);
  const [product, setProduct] = useState<ProductRecord | null>(null);
  const [productForm, setProductForm] = useState<ProductFormState>(emptyProductForm);
  const [priceForm, setPriceForm] = useState<PriceFormState>(emptyPriceForm);
  const [priceCreateOpen, setPriceCreateOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmDeletePrice, setConfirmDeletePrice] = useState(false);

  async function loadProduct() {
    if (!tokens?.access_token || !productId) {
      return;
    }

    setStatus('loading');
    try {
      const [productResponse, applicationsResponse] = await Promise.all([
        billingApi.getProduct(tokens.access_token, productId),
        billingApi.listApplications(tokens.access_token),
      ]);
      setApplications(applicationsResponse.items);
      setProduct(productResponse);
      setProductForm({
        id: productResponse.id,
        application_id: productResponse.application_id,
        key: productResponse.key,
        display_name: productResponse.display_name,
        description: productResponse.description ?? '',
        product_type: productResponse.product_type,
        status: productResponse.status,
        visibility: productResponse.visibility,
        is_featured: productResponse.is_featured,
        metadataText: formatMetadata(productResponse),
        providerMappingsText: formatProviderMappings(productResponse),
      });
      setStatus('success');
    } catch (error) {
      setBanner({ error: true, message: describeError(error) });
      setStatus('error');
    }
  }

  useEffect(() => {
    void loadProduct();
  }, [productId, tokens?.access_token]);

  const priceColumns = useMemo<TableColumn<PriceRecord>[]>(
    () => [
      { key: 'display_name', label: 'billing.column.displayName' },
      {
        key: 'amount',
        label: 'billing.column.amount',
        render: (price) => `${price.currency} ${price.amount}`,
        sortValue: (price) => price.amount,
      },
      { key: 'currency', label: 'billing.column.currency', maxWidth: 100 },
      {
        key: 'billing_interval',
        label: 'billing.column.billingInterval',
        maxWidth: 120,
      },
      {
        key: 'status',
        label: 'billing.column.status',
        render: (price) => optionLabel(priceStatusOptions, price.status),
        maxWidth: 120,
      },
      {
        key: 'provider_status',
        label: 'billing.column.providerMappings',
        render: (price) =>
          price.provider_mappings.find((mapping) => mapping.status === 'active')?.status ??
          price.provider_mappings[0]?.status ??
          '—',
      },
      { key: 'updated_at', label: 'billing.resource.updatedAt', maxWidth: 180 },
      {
        key: 'actions',
        label: 'billing.column.actions',
        className: 'table-actions-cell',
        truncate: false,
        render: (price) => (
          <DataViewActionCell>
            <ActionIconButton
              icon="manage"
              label={t('billing.ui.price.settings')}
              onClick={() => openPriceSettings(price)}
            />
          </DataViewActionCell>
        ),
      },
    ],
    [navigate, productId],
  );

  const providerMappingColumns = useMemo<TableColumn<ProductRecord['provider_mappings'][number]>[]>(
    () => [
      { key: 'provider_key', label: 'billing.column.provider' },
      { key: 'environment', label: 'billing.column.details', maxWidth: 120 },
      { key: 'status', label: 'billing.column.status', maxWidth: 120 },
      {
        key: 'provider_product_id',
        label: 'billing.column.product',
        render: (mapping) => mapping.provider_product_id ?? '—',
      },
      {
        key: 'provider_price_id',
        label: 'billing.column.prices',
        render: (mapping) => mapping.provider_price_id ?? '—',
      },
    ],
    [],
  );

  async function submitProduct(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!tokens?.access_token || !product) {
      return;
    }

    setBusy(true);
    try {
      await billingApi.updateProduct(tokens.access_token, product.id, toProductInput(productForm));
      toast.success(t('billing.ui.product.updated'));
      await loadProduct();
    } catch (error) {
      toast.error(describeError(error));
    } finally {
      setBusy(false);
    }
  }

  async function deleteProduct() {
    if (!tokens?.access_token || !product) {
      return;
    }

    setBusy(true);
    try {
      await billingApi.deleteProduct(tokens.access_token, product.id);
      toast.success(t('billing.ui.product.deleted'));
      navigate('/products');
    } catch (error) {
      toast.error(describeError(error));
    } finally {
      setBusy(false);
    }
  }

  if (!tokens) {
    return (
      <section className="panel page-panel">
        {t('billing.ui.your.session.is.missing.or.expired')}
      </section>
    );
  }

  if (status === 'loading') {
    return <section className="panel page-panel">{t('billing.ui.loading.product')}</section>;
  }

  if (status === 'error' || !product) {
    return (
      <section className="page">
        <PageHeader
          breadcrumbs={<Link to="/products">{t('billing.nav.products')}</Link>}
          title={t('billing.ui.product.not.found')}
        />
        <section className="panel panel-error page-panel">
          {banner?.message ?? t('billing.ui.the.requested.product.could.not.be.loaded')}
        </section>
      </section>
    );
  }

  const selectedPrice = priceId
    ? (product.prices.find((price) => price.id === priceId) ?? null)
    : null;
  const activePriceForm =
    selectedPrice && priceForm.id !== selectedPrice.id ? formatPrice(selectedPrice) : priceForm;

  function openCreatePrice() {
    if (!product) {
      return;
    }

    setPriceForm({
      ...emptyPriceForm,
      billing_product_id: product.id,
      sort_order: String(product.prices.length + 1),
    });
    setPriceCreateOpen(true);
  }

  function openPriceSettings(price: PriceRecord) {
    setPriceForm(formatPrice(price));
    navigate(`/products/${productId}/prices/${price.id}`);
  }

  async function submitPrice(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!tokens?.access_token || !product) {
      return;
    }

    setBusy(true);
    try {
      if (activePriceForm.id) {
        await billingApi.updatePrice(
          tokens.access_token,
          activePriceForm.id,
          toPriceInput(activePriceForm),
        );
        toast.success(t('billing.ui.price.updated'));
      } else {
        const created = await billingApi.createPrice(
          tokens.access_token,
          toPriceInput({ ...activePriceForm, billing_product_id: product.id }),
        );
        toast.success(t('billing.ui.price.created'));
        setPriceCreateOpen(false);
        navigate(`/products/${product.id}/prices/${created.id}`);
      }
      await loadProduct();
    } catch (error) {
      toast.error(describeError(error));
    } finally {
      setBusy(false);
    }
  }

  async function deletePrice() {
    if (!tokens?.access_token || !activePriceForm.id || !product) {
      return;
    }

    setBusy(true);
    try {
      await billingApi.deletePrice(tokens.access_token, activePriceForm.id);
      toast.success(t('billing.ui.price.deleted'));
      setConfirmDeletePrice(false);
      navigate(`/products/${product.id}`);
      await loadProduct();
    } catch (error) {
      toast.error(describeError(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="page" data-testid="billing-product-manage-page">
      <PageHeader
        breadcrumbs={
          <>
            <Link to="/products">{t('billing.nav.products')}</Link>
            <span>/</span>
            <span>{product.display_name}</span>
          </>
        }
        description={t(
          'billing.ui.manage.product.metadata.visibility.prices.and.provider.mappings.from.one.place',
        )}
        title={product.display_name}
      />

      <Section title={t('billing.ui.basic.information')}>
        <form onSubmit={(event) => void submitProduct(event)}>
          <ProductFormFields
            applications={applications}
            form={productForm}
            onChange={setProductForm}
          />
          <div className="dialog-actions">
            <Button disabled={busy} type="submit">
              {t('billing.ui.save.product')}
            </Button>
            <Button
              disabled={busy}
              onClick={() => setConfirmDelete(true)}
              type="button"
              variant="danger"
            >
              {t('billing.ui.delete.product')}
            </Button>
          </div>
        </form>
      </Section>

      <Section
        actions={
          <Button onClick={openCreatePrice} type="button">
            {t('billing.ui.create.price')}
          </Button>
        }
        title={t('billing.column.prices')}
      >
        <DataTable
          columns={priceColumns}
          emptyMessage={t('billing.ui.no.prices.are.attached.to.this.product')}
          pageSize={5}
          rows={product.prices}
          searchable
          searchPlaceholder={t('billing.ui.search.prices')}
        />
      </Section>

      <Section title={t('billing.column.providerMappings')}>
        <DataTable
          columns={providerMappingColumns}
          emptyMessage={t('billing.ui.no.provider.mappings.are.configured')}
          pageSize={5}
          rows={product.provider_mappings}
          searchable
          searchPlaceholder={t('billing.ui.search.provider.mappings')}
        />
      </Section>

      <EntityManageDialog
        onClose={() => {
          setPriceForm(emptyPriceForm);
          navigate(`/products/${product.id}`);
        }}
        open={Boolean(selectedPrice)}
        size="large"
        title={
          selectedPrice
            ? t('billing.price.settingsTitle', {
                name: selectedPrice.display_name,
              })
            : t('billing.ui.price.settings')
        }
      >
        {selectedPrice ? (
          <form onSubmit={(event) => void submitPrice(event)}>
            <PriceFormFields form={activePriceForm} onChange={setPriceForm} />
            <div className="dialog-actions">
              <Button disabled={busy} type="submit">
                {t('billing.ui.save.price')}
              </Button>
              <Button
                disabled={busy}
                onClick={() => setConfirmDeletePrice(true)}
                type="button"
                variant="danger"
              >
                {t('billing.ui.delete.price')}
              </Button>
            </div>
          </form>
        ) : null}
      </EntityManageDialog>

      <EntityCreateDialog
        onClose={() => {
          setPriceCreateOpen(false);
          setPriceForm(emptyPriceForm);
        }}
        open={priceCreateOpen}
        size="large"
        title={t('billing.ui.create.price')}
      >
        <form onSubmit={(event) => void submitPrice(event)}>
          <PriceFormFields form={priceForm} onChange={setPriceForm} />
          <div className="dialog-actions">
            <Button disabled={busy} type="submit">
              {t('billing.ui.create.price')}
            </Button>
            <Button
              disabled={busy}
              onClick={() => {
                setPriceCreateOpen(false);
                setPriceForm(emptyPriceForm);
              }}
              type="button"
              variant="secondary"
            >
              {t('billing.ui.cancel')}
            </Button>
          </div>
        </form>
      </EntityCreateDialog>

      <ConfirmDialog
        busy={busy}
        description={t('billing.ui.this.will.permanently.remove.the.product')}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => void deleteProduct()}
        open={confirmDelete}
        title={t('billing.ui.delete.product')}
      />
      <ConfirmDialog
        busy={busy}
        description={t('billing.ui.this.will.permanently.remove.the.price')}
        onClose={() => setConfirmDeletePrice(false)}
        onConfirm={() => void deletePrice()}
        open={confirmDeletePrice}
        title={t('billing.ui.delete.price')}
      />
    </section>
  );
}
